import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Search, Edit2, Trash2, X, Check } from 'lucide-react';
import {
  listAnimalsByIdentifier, insertAnimalRow, updateAnimalRow, deleteAnimal,
  newAnimalId,
} from '../../../data/animals';
import { currentUserId } from '../../../data/auth';
import { COMMON_BREEDS, ANIMAL_CATEGORIES } from '../../../constants/workflowGranular';
import { MARK } from '../../../constants';
import { racaPadrao } from '../../../utils/animais';
import { describeError } from '../../../data/errors';
import JanelaModal from '../../JanelaModal';
import CruzamentosDoAnimal from '../modals/CruzamentosDoAnimal';

// ─── Inline edit modal ───────────────────────────────────────────────────────

const CATEGORY_OPTIONS = Object.values(ANIMAL_CATEGORIES);
const SEX_OPTIONS = [
  { value: 'F', label: 'Fêmea' },
  { value: 'M', label: 'Macho' },
];

const AnimalModal = ({ animal, clients, onSave, onClose, loading }) => {
  const isNew = !animal?.id;
  const [form, setForm] = useState({
    identifier: animal?.identifier || '',
    breed: animal?.breed || '',
    customBreed: '',
    category: animal?.category || '',
    sex: animal?.sex || '',
    rgd: animal?.rgd || '',
    client_id: animal?.client_id || '',
  });
  const [errors, setErrors] = useState({});

  // Se a raça atual não está na lista fixa, é uma raça customizada
  useEffect(() => {
    if (animal?.breed && !COMMON_BREEDS.includes(animal.breed)) {
      setForm(f => ({ ...f, breed: 'Outra', customBreed: animal.breed }));
    }
  }, [animal?.breed]);

  const set = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
    if (errors[field]) setErrors(e => ({ ...e, [field]: null }));
  };

  const validate = () => {
    const errs = {};
    if (!form.identifier.trim()) errs.identifier = 'Obrigatório';
    const effectiveBreed = form.breed === 'Outra' ? form.customBreed.trim() : form.breed.trim();
    if (!effectiveBreed) errs.breed = form.breed === 'Outra' ? 'Digite o nome da raça' : 'Obrigatório';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    const effectiveBreed = form.breed === 'Outra' ? form.customBreed.trim() : form.breed.trim();
    onSave({
      identifier: form.identifier.trim(),
      breed: effectiveBreed,
      category: form.category || null,
      sex: form.sex || null,
      rgd: form.rgd.trim() || null,
      client_id: form.client_id || null,
    });
  };

  return (
    <JanelaModal chave="animal" titulo={isNew ? 'Novo Animal' : 'Editar Animal'}
      onFechar={onClose} largura="sm:max-w-md">

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Identificação */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Identificação <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="Brinco, nome ou número"
              autoFocus
              className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 ${errors.identifier ? 'border-red-300 focus:ring-red-400' : 'border-slate-300 focus:ring-blue-500'}`}
              value={form.identifier}
              onChange={e => set('identifier', e.target.value)}
            />
            {errors.identifier && <p className="text-xs text-red-600 mt-1">{MARK.WARN} {errors.identifier}</p>}
          </div>

          {/* Raça */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Raça <span className="text-red-500">*</span>
            </label>
            <select
              className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 ${errors.breed ? 'border-red-300 focus:ring-red-400' : 'border-slate-300 focus:ring-blue-500'}`}
              value={form.breed}
              onChange={e => { set('breed', e.target.value); if (e.target.value !== 'Outra') set('customBreed', ''); }}
            >
              <option value="">Selecione...</option>
              {COMMON_BREEDS.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
            {form.breed === 'Outra' && (
              <input
                type="text"
                placeholder="Digite o nome da raça..."
                autoFocus
                className={`mt-2 w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 ${errors.breed ? 'border-red-300 focus:ring-red-400' : 'border-slate-300 focus:ring-blue-500'}`}
                value={form.customBreed}
                onChange={e => { set('customBreed', e.target.value); if (errors.breed) setErrors(er => ({ ...er, breed: null })); }}
              />
            )}
            {errors.breed && <p className="text-xs text-red-600 mt-1">{MARK.WARN} {errors.breed}</p>}
          </div>

          {/* Categoria + Sexo lado a lado */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Categoria</label>
              <select
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={form.category}
                onChange={e => {
                  set('category', e.target.value);
                  if (isNew && (!form.breed || form.breed === racaPadrao(form.category))) {
                    set('breed', racaPadrao(e.target.value));
                  }
                }}
              >
                <option value="">Não especificar</option>
                {CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Sexo</label>
              <select
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={form.sex}
                onChange={e => set('sex', e.target.value)}
              >
                <option value="">Não especificar</option>
                {SEX_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>

          {/* Cliente */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Cliente / Fazenda</label>
            <select
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={form.client_id}
              onChange={e => set('client_id', e.target.value)}
            >
              <option value="">Sem vínculo</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          {/* RGD */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">RGD <span className="text-slate-400 font-normal text-xs">(opcional)</span></label>
            <input
              type="text"
              placeholder="Registro genealógico"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={form.rgd}
              onChange={e => set('rgd', e.target.value)}
            />
          </div>
        </div>

        <div className="flex gap-3 p-4 border-t border-slate-200">
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium text-sm"
          >
            <Check size={16} />
            {loading ? 'Salvando...' : isNew ? 'Cadastrar' : 'Salvar alterações'}
          </button>
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 text-sm"
          >
            Cancelar
          </button>
        </div>
    </JanelaModal>
  );
};

// ─── Main Tab ────────────────────────────────────────────────────────────────

const CATEGORY_FILTERS = ['Todos', ...Object.values(ANIMAL_CATEGORIES)];

const AnimalsTab = ({ laboratoryId, clients, canEdit }) => {
  const [animals, setAnimals]     = useState([]);
  const [loading, setLoading]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [search, setSearch]       = useState('');
  const [categoryFilter, setCategoryFilter] = useState('Todos');
  const [editingAnimal, setEditingAnimal]   = useState(null); // null = fechado, {} = novo, {...} = editar
  const [confirmDelete, setConfirmDelete]   = useState(null); // id para confirmar exclusão
  const [cruzamentosDe, setCruzamentosDe]   = useState(null); // animal da ficha de cruzamentos

  const load = useCallback(async () => {
    if (!laboratoryId) return;
    setLoading(true);
    try {
      setAnimals(await listAnimalsByIdentifier(laboratoryId) || []);
    } catch (err) {
      console.error('Erro ao carregar animais:', err);
    } finally {
      setLoading(false);
    }
  }, [laboratoryId]);

  useEffect(() => { load(); }, [load]);

  /**
   * Filtro local.
   *
   * O RGD entrou porque a busca do BANCO já casava por ele desde sempre
   * (`search_animals` faz `identifier ILIKE ... OR rgd ILIKE ...`), e era só
   * esta lista que não — quem procurava pelo registro achava o animal dentro
   * do modal de etapas e não achava na aba que existe para procurar animal.
   */
  const filtered = animals.filter(a => {
    const alvo = search.toLowerCase();
    const matchSearch = !search.trim() ||
      a.identifier?.toLowerCase().includes(alvo) ||
      a.rgd?.toLowerCase().includes(alvo) ||
      a.breed?.toLowerCase().includes(alvo) ||
      clients.find(c => c.id === a.client_id)?.name?.toLowerCase().includes(alvo);
    const matchCat = categoryFilter === 'Todos' || a.category === categoryFilter;
    return matchSearch && matchCat;
  });

  const handleSave = async (formData) => {
    setSaving(true);
    try {
      if (editingAnimal?.id) {
        // Atualizar
        await updateAnimalRow(editingAnimal.id, formData, laboratoryId);
      } else {
        // Criar
        await insertAnimalRow({ id: newAnimalId(), ...formData, laboratory_id: laboratoryId, created_by: await currentUserId() });
      }
      await load();
      setEditingAnimal(null);
    } catch (err) {
      alert(describeError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setSaving(true);
    try {
      await deleteAnimal(id, laboratoryId);
      setAnimals(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      alert(describeError(err));
    } finally {
      setSaving(false);
      setConfirmDelete(null);
    }
  };

  const clientName = (id) => clients.find(c => c.id === id)?.name || '—';

  const catColor = (cat) => {
    const map = {
      'Doadora':    'bg-slate-100 text-slate-700',
      'Receptora':  'bg-slate-100 text-slate-700',
      'Reprodutor': 'bg-blue-100 text-blue-700',
      'Touro':      'bg-slate-100 text-slate-700',
      'Ambos':      'bg-slate-100 text-slate-700',
    };
    return map[cat] || 'bg-slate-100 text-slate-600';
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Animais</h2>
          <p className="text-sm text-slate-500">{animals.length} cadastrado{animals.length !== 1 ? 's' : ''}</p>
        </div>
        {canEdit() && (
          <button
            onClick={() => setEditingAnimal({})}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium"
          >
            <Plus size={16} />
            Novo Animal
          </button>
        )}
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg border border-slate-200 p-3 flex flex-col sm:flex-row gap-3">
        {/* Busca */}
        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Buscar por brinco, RGD, raça ou cliente..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              <X size={13} />
            </button>
          )}
        </div>

        {/* Categoria */}
        <div className="flex flex-wrap gap-1.5">
          {CATEGORY_FILTERS.map(cat => (
            <button
              key={cat}
              onClick={() => setCategoryFilter(cat)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                categoryFilter === cat
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        <span className="ml-auto text-xs text-slate-500 self-center whitespace-nowrap">
          {filtered.length !== animals.length ? <><b>{filtered.length}</b> de {animals.length}</> : <b>{filtered.length}</b>} animal{filtered.length !== 1 ? 'is' : ''}
        </span>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-slate-400 text-sm">Carregando...</div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-slate-400 text-sm">
            {animals.length === 0 ? 'Nenhum animal cadastrado ainda.' : 'Nenhum animal encontrado com esses filtros.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left">
                  <th className="px-4 py-2.5 font-medium text-slate-700">Identificação</th>
                  <th className="px-4 py-2.5 font-medium text-slate-700">Raça</th>
                  <th className="px-4 py-2.5 font-medium text-slate-700">Categoria</th>
                  <th className="px-4 py-2.5 font-medium text-slate-700">Sexo</th>
                  <th className="px-4 py-2.5 font-medium text-slate-700">Cliente</th>
                  <th className="px-4 py-2.5 font-medium text-slate-700">RGD</th>
                  {canEdit() && <th className="px-4 py-2.5 w-20" />}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map(animal => (
                  <tr key={animal.id} onClick={() => setCruzamentosDe(animal)}
                    title="Ver cruzamentos"
                    className="hover:bg-slate-50 transition-colors cursor-pointer">
                    <td className="px-4 py-3 font-medium text-slate-900">{animal.identifier}</td>
                    <td className="px-4 py-3 text-slate-700">{animal.breed || '—'}</td>
                    <td className="px-4 py-3">
                      {animal.category
                        ? <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${catColor(animal.category)}`}>{animal.category}</span>
                        : <span className="text-slate-400 text-xs">—</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-600 text-xs">
                      {animal.sex === 'F' ? 'Fêmea' : animal.sex === 'M' ? 'Macho' : '—'}
                    </td>
                    <td className="px-4 py-3 text-slate-600 truncate max-w-[140px]">{clientName(animal.client_id)}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{animal.rgd || '—'}</td>
                    {canEdit() && (
                      <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                        {confirmDelete === animal.id ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleDelete(animal.id)}
                              disabled={saving}
                              className="text-xs bg-red-600 text-white px-2 py-1 rounded hover:bg-red-700 disabled:opacity-50"
                            >
                              Confirmar
                            </button>
                            <button onClick={() => setConfirmDelete(null)} className="text-xs text-slate-500 hover:text-slate-700 px-1">
                              <X size={14} />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setEditingAnimal(animal)}
                              className="text-blue-600 hover:text-blue-800"
                              title="Editar"
                            >
                              <Edit2 size={15} />
                            </button>
                            <button
                              onClick={() => setConfirmDelete(animal.id)}
                              className="text-slate-400 hover:text-red-600 transition-colors"
                              title="Excluir"
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {cruzamentosDe && (
        <CruzamentosDoAnimal animal={cruzamentosDe} laboratoryId={laboratoryId}
          onClose={() => setCruzamentosDe(null)} />
      )}

      {/* Modal de edição/criação */}
      {editingAnimal !== null && (
        <AnimalModal
          animal={editingAnimal?.id ? editingAnimal : null}
          clients={clients}
          loading={saving}
          onSave={handleSave}
          onClose={() => setEditingAnimal(null)}
        />
      )}
    </div>
  );
};

export default AnimalsTab;
